// Native (iPhone app) helpers. The website never takes these branches.
import { Capacitor } from '@capacitor/core';

export const isNative = Capacitor.isNativePlatform();

// The app bundles only the React app, so the site's own pages (Terms, Privacy)
// open from the live site; Capacitor sends them to Safari. They use medxmeet.web.app
// because every medxmeet.com link is a universal link back into the app (Firebase
// Hosting serves its own apple-app-site-association covering /*).
export const SITE = 'https://medxmeet.web.app';
export const siteUrl = (path) => (isNative ? `${SITE}${path}` : path);

// The iOS launch splash stays up (launchAutoHide: false) until the first real screen
// is ready: the sign-in page, or the dashboard once the profile has loaded.
export const hideSplash = () => {
  if (!isNative) return;
  import('@capacitor/splash-screen')
    .then(({ SplashScreen }) => SplashScreen.hide({ fadeOutDuration: 200 }))
    .catch(() => {});
};

// Universal links (medxmeet.com/?join=…) that open the app: the one it launched with,
// then any while it runs. handler gets a URL. Returns a cleanup function.
let launchLinkUsed = false;
export const onAppLink = (handler) => {
  if (!isNative) return () => {};
  let handle = null;
  let stopped = false;
  // Only medxmeet.com/ belongs to the app; any other page (Privacy, Support, …) is
  // handed to Safari on the web.app address, which doesn't come back here.
  const route = (url) => {
    if (url.pathname === '/' || url.pathname === '') handler(url);
    else window.open(`${SITE}${url.pathname}${url.search}${url.hash}`, '_blank');
  };
  import('@capacitor/app').then(async ({ App }) => {
    const launch = await App.getLaunchUrl().catch(() => null);
    if (stopped) return;
    // Only once: signing out and in again mustn't reopen the launch link.
    if (launch?.url && !launchLinkUsed) { launchLinkUsed = true; route(new URL(launch.url)); }
    handle = await App.addListener('appUrlOpen', ({ url }) => route(new URL(url)));
    if (stopped) handle.remove();
  }).catch(() => {});
  return () => { stopped = true; handle?.remove(); };
};

// Haptics (brief 028): a light tap for sends, answers and tab changes; a success
// buzz when a session is confirmed. Nothing happens on the website.
export const tapFeedback = () => {
  if (!isNative) return;
  import('@capacitor/haptics')
    .then(({ Haptics, ImpactStyle }) => Haptics.impact({ style: ImpactStyle.Light }))
    .catch(() => {});
};
export const successFeedback = () => {
  if (!isNative) return;
  import('@capacitor/haptics')
    .then(({ Haptics, NotificationType }) => Haptics.notification({ type: NotificationType.Success }))
    .catch(() => {});
};

// A file name that's safe on disk and keeps its extension (Quick Look needs it).
export const localFileName = (name, storagePath) => {
  const raw = (name || (storagePath || '').split('/').pop() || 'document').replace(/^\d+-/, '');
  return raw.replace(/[^\w.\- ]+/g, '_').replace(/^\.+/, '').slice(-120) || 'document';
};

// Downloads a shared document into the app's cache; returns its file path.
const fetchDocument = async (url, name, storagePath) => {
  const { Filesystem, Directory } = await import('@capacitor/filesystem');
  const { path } = await Filesystem.downloadFile({
    url,
    path: `documents/${Date.now()}-${localFileName(name, storagePath)}`,
    directory: Directory.Cache,
    recursive: true,
  });
  return path;
};

// In the app, a shared document opens in iOS's own viewer (Quick Look; the X
// returns to MedXMeet) instead of inside the web view. Resolves true when shown;
// false on the website, where the caller opens the URL as before.
export const openDocument = async (url, name, storagePath) => {
  if (!isNative) return false;
  const path = await fetchDocument(url, name, storagePath);
  const { FileOpener } = await import('@capacitor-community/file-opener');
  await FileOpener.open({ filePath: path, openWithDefault: true });
  return true;
};

// In the app, the download button opens the iOS share sheet (Save to Files, AirDrop,
// Mail, …). Resolves true when shown; false on the website.
export const saveDocument = async (url, name, storagePath) => {
  if (!isNative) return false;
  const path = await fetchDocument(url, name, storagePath);
  const { Share } = await import('@capacitor/share');
  await Share.share({ files: [path] }).catch(e => {
    // Closing the sheet without choosing anything isn't an error.
    if (!/cancel/i.test(e?.message || '')) throw e;
  });
  return true;
};

// Share a link (brief 036; used by "Share MedXMeet" and "Invite a study partner").
// iPhone app: the iOS share sheet. Website: the browser's share sheet where there is
// one (phones), else the link is copied and a "Link copied" toast shows.
// Resolves 'shared', 'copied', 'cancelled' or 'failed'.
export const shareLink = async ({ title, text, url }) => {
  const cancelled = e => e?.name === 'AbortError' || /cancel/i.test(e?.message || '');
  try {
    if (isNative) {
      const { Share } = await import('@capacitor/share');
      await Share.share({ title, text, url, dialogTitle: title });
      return 'shared';
    }
    if (navigator.share) {
      await navigator.share({ title, text, url });
      return 'shared';
    }
  } catch (e) {
    if (cancelled(e)) return 'cancelled';
    if (isNative) return 'failed';
    // Web Share refused (e.g. not allowed here): fall through to copying.
  }
  const { toast } = await import('./components/feedback');
  try {
    await navigator.clipboard.writeText(text && !text.includes(url) ? `${text} ${url}` : (text || url));
    toast('Link copied');
    return 'copied';
  } catch {
    toast(`Copy this link: ${url}`, { duration: 10000 });
    return 'failed';
  }
};
